/**
 * Turns whatever went wrong into something a customer can act on.
 *
 * The app was showing raw Supabase/Postgres text straight from `err.message`
 * — "new row violates row-level security policy for table \"posts\"", or
 * "duplicate key value violates unique constraint" — in user-facing toasts.
 * That tells a developer plenty and a customer nothing, and it leaks table
 * and constraint names to anyone reading their own screen.
 *
 * The rule here is deliberately conservative: unless a message is
 * recognisably ours, it does not reach the user. A technical string is
 * translated when we can do better than the caller's fallback, and dropped
 * in favour of that fallback when we can't.
 *
 * The original is always preserved on the console, so debugging a report
 * doesn't get harder.
 */

/** Substrings that mark a message as written for developers, not people. */
const TECHNICAL_MARKERS = [
  "row-level security",
  "violates",
  "constraint",
  "duplicate key",
  "relation ",
  "column ",
  "syntax error",
  "invalid input",
  "permission denied",
  "pgrst",
  "jwt",
  "supabase",
  "postgres",
  "sql",
  "null value in column",
  "function ",
  "schema ",
  "does not exist",
  "failed to parse",
  "unexpected token",
];

/** Recognised conditions worth a better message than the caller's fallback. */
const TRANSLATIONS: { match: RegExp; message: string }[] = [
  {
    match: /network request failed|fetch failed|failed to fetch|timeout|econnreset|offline/i,
    message: "You seem to be offline. Check your connection and try again.",
  },
  {
    match: /jwt|token is expired|invalid claim|not authenticated|session/i,
    message: "Your session has expired. Please sign in again.",
  },
  {
    match: /row-level security|permission denied|not authorized|42501/i,
    message: "You don't have permission to do that.",
  },
  {
    match: /duplicate key|already exists|23505/i,
    message: "That already exists.",
  },
  {
    match: /foreign key|23503/i,
    message: "That's still linked to something else, so it can't be changed.",
  },
  {
    match: /payload too large|413|exceeded the maximum|file size/i,
    message: "That file is too large.",
  },
  {
    match: /rate limit|too many requests|429/i,
    message: "Too many attempts just now. Give it a moment and try again.",
  },
];

const looksTechnical = (message: string): boolean => {
  const lower = message.toLowerCase();
  return TECHNICAL_MARKERS.some((marker) => lower.includes(marker));
};

/**
 * @param error    Whatever was caught.
 * @param fallback What to say when the underlying message isn't usable.
 */
export function friendlyError(error: unknown, fallback: string): string {
  const raw =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : (error as any)?.message;

  if (!raw || typeof raw !== "string") return fallback;

  // Kept for the developer even when the user sees something else.
  if (process.env.NODE_ENV !== "production") console.log("[error]", raw);

  for (const { match, message } of TRANSLATIONS) {
    if (match.test(raw)) return message;
  }

  // A Postgres error code is a reliable tell that this came from the
  // database rather than from our own `throw new Error(...)`.
  const code = (error as any)?.code;
  if (typeof code === "string" && /^[0-9A-Z]{5}$/.test(code)) return fallback;

  if (looksTechnical(raw)) return fallback;

  // Long strings are rarely ours — our own messages are one short sentence.
  if (raw.length > 140) return fallback;

  return raw;
}
